import { useState, useEffect } from 'react';
import { getOrders, getProducts, getEnquiries, getConsignments, getCategories } from '../lib/api';
import { BASE_URL } from '../lib/env';

function getToken() {
  return localStorage.getItem('aurora_admin_token') || '';
}

// Fetch ALL orders (bypasses the default 10-row pagination cap)
async function getAllOrders() {
  const res = await fetch(`${BASE_URL}/index.php?route=orders&page=1&limit=9999`, {
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
  });
  const data = await res.json();
  return Array.isArray(data) ? data : data?.orders || [];
}

export default function SectionAnalytics() {
  const [orders,       setOrders]       = useState([]);
  const [products,     setProducts]     = useState([]);
  const [enquiries,    setEnquiries]    = useState([]);
  const [consignments, setConsignments] = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [range,        setRange]        = useState('30');
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    Promise.all([
      getAllOrders().catch(() => []),          // ← all orders, no pagination cap
      getProducts().catch(() => []),
      getEnquiries().catch(() => []),
      getConsignments().catch(() => []),
      getCategories().catch(() => []),
    ]).then(([o, p, e, c, cats]) => {
      setOrders(Array.isArray(o) ? o : o?.orders || []);
      setProducts(Array.isArray(p) ? p : []);
      setEnquiries(Array.isArray(e) ? e : []);
      setConsignments(Array.isArray(c) ? c : []);
      setCategories(Array.isArray(cats) ? cats : []);
    }).finally(() => setLoading(false));
  }, []);

  // ── Filter by date range ──────────────────────────────
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - Number(range));

  const filtered = orders.filter(o => {
    if (!o.created_at) return true;
    return new Date(o.created_at) >= cutoff;
  });

  // ── Order status breakdown ────────────────────────────
  const STATUS_COLORS = {
    processing: '#f59e0b',
    shipped:    '#3b82f6',
    delivered:  '#22c55e',
    cancelled:  '#e5e7eb',
  };

  const statusCounts = { processing: 0, shipped: 0, delivered: 0, cancelled: 0 };
  filtered.forEach(o => {
    const k = (o.status || '').toLowerCase();
    if (k in statusCounts) statusCounts[k]++;
  });
  const maxStatus = Math.max(...Object.values(statusCounts), 1);

  // ── Only DELIVERED orders count toward revenue ────────
  const delivered = filtered.filter(o => (o.status || '').toLowerCase() === 'delivered');

  // ── Category color map ────────────────────────────────
  const CATEGORY_COLORS = [
    '#c9a84c', '#1a1a1a', '#3b82f6', '#22c55e',
    '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4',
    '#ec4899', '#14b8a6', '#f97316', '#84cc16',
  ];

  const categoryColorMap = {};
  categories.forEach((cat, i) => {
    categoryColorMap[cat.name] = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
  });

  // ── Revenue by category (delivered only) ─────────────
  const revByCategory = {};

  delivered.forEach(o => {
    const items = Array.isArray(o.items) ? o.items : [];
    items.forEach(item => {
      const prod = products.find(p =>
        p.name?.toLowerCase() === item.name?.toLowerCase() ||
        p.id === item.product_id ||
        (item.sku && p.sku === item.sku)
      );
      const rawCat = prod?.category || item.category || '';
      const matched = categories.find(c =>
        c.name?.toLowerCase() === rawCat?.toLowerCase()
      );
      const cat = matched?.name || (rawCat || 'Other');
      revByCategory[cat] = (revByCategory[cat] || 0) + (Number(item.price) * (item.qty || 1));
    });
    if (!items.length && o.total) {
      revByCategory['Other'] = (revByCategory['Other'] || 0) + Number(o.total);
    }
  });

  // All categories that have revenue, sorted descending — no slice so we scroll
  const revEntries = Object.entries(revByCategory).sort((a, b) => b[1] - a[1]);
  const maxRev     = Math.max(...revEntries.map(e => e[1]), 1);

  // ── Top products (delivered only) ────────────────────
  const productSales = {};
  delivered.forEach(o => {
    const items = Array.isArray(o.items) ? o.items : [];
    items.forEach(item => {
      const key = item.name || 'Unknown';
      if (!productSales[key]) productSales[key] = { sold: 0, revenue: 0, brand: item.brand || '' };
      productSales[key].sold    += (item.qty || 1);
      productSales[key].revenue += Number(item.price) * (item.qty || 1);
    });
  });

  const topProducts = Object.entries(productSales)
    .sort((a, b) => b[1].revenue - a[1].revenue)
    .slice(0, 5);

  // ── Avg order value (delivered only) ─────────────────
  const totalRevenue = delivered.reduce((s, o) => s + Number(o.total || 0), 0);
  const avgOrderVal  = delivered.length ? Math.round(totalRevenue / delivered.length) : 0;

  // ── Lead sources ──────────────────────────────────────
  const enqCount  = enquiries.filter(e => { if (!e.created_at) return true; return new Date(e.created_at) >= cutoff; }).length;
  const consCount = consignments.filter(c => { if (!c.created_at) return true; return new Date(c.created_at) >= cutoff; }).length;

  const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

  const cardStyle = {
    background: '#fff',
    border: '1px solid #e8e4de',
    borderRadius: 12,
    padding: '28px 28px',
  };

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', fontSize: 12, color: 'var(--grey-mid)' }}>
      Loading analytics…
    </div>
  );

  // Each category row is ~52px tall. Show 6 before scrolling.
  const ROW_HEIGHT  = 52;
  const MAX_VISIBLE = 6;
  const catAreaHeight = revEntries.length > MAX_VISIBLE
    ? ROW_HEIGHT * MAX_VISIBLE
    : 'auto';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(36px, 5vw, 52px)',
            fontWeight: 300,
            color: 'var(--dark)',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: 0,
          }}>
            Analytics
          </h1>
          <p style={{ fontSize: 13, color: 'var(--grey-mid)', marginTop: 6, fontFamily: 'var(--font-body)' }}>
            Deep dive into store performance.
          </p>
        </div>

        <select
          value={range}
          onChange={e => setRange(e.target.value)}
          style={{
            fontSize: 13, border: '1px solid #e5e7eb', padding: '10px 16px',
            background: '#fff', color: 'var(--dark)', outline: 'none',
            cursor: 'pointer', fontFamily: 'var(--font-body)', borderRadius: 6,
          }}>
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="90">Last 90 Days</option>
          <option value="365">Last 12 Months</option>
        </select>
      </div>

      {/* ── Row 1: Order Status + Revenue by Category ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Order Status Breakdown */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)', margin: '0 0 24px', fontFamily: 'var(--font-body)' }}>
            Order Status Breakdown
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {Object.entries(statusCounts).map(([status, count]) => (
              <div key={status}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                  <span style={{ fontSize: 13, color: 'var(--dark)', fontFamily: 'var(--font-body)', textTransform: 'capitalize' }}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', fontFamily: 'var(--font-body)' }}>
                    {count}
                  </span>
                </div>
                <div style={{ width: '100%', height: 6, background: '#f3f0eb', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    width: `${(count / maxStatus) * 100}%`,
                    height: '100%',
                    background: STATUS_COLORS[status],
                    borderRadius: 3,
                    transition: 'width 0.6s ease',
                    minWidth: count > 0 ? 6 : 0,
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue by Category */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)', margin: 0, fontFamily: 'var(--font-body)' }}>
              Revenue by Category
            </h2>
            {revEntries.length > MAX_VISIBLE && (
              <span style={{ fontSize: 11, color: 'var(--grey-mid)', fontFamily: 'var(--font-body)' }}>
                {revEntries.length} categories · scroll ↕
              </span>
            )}
          </div>

          {revEntries.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--grey-mid)' }}>No revenue data yet.</p>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
              maxHeight: catAreaHeight,
              overflowY: revEntries.length > MAX_VISIBLE ? 'auto' : 'visible',
              paddingRight: revEntries.length > MAX_VISIBLE ? 6 : 0,
            }}>
              {revEntries.map(([cat, rev]) => {
                const color = categoryColorMap[cat] || '#9ca3af';
                const pct   = Math.round((rev / maxRev) * 100);
                return (
                  <div key={cat}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: 'var(--dark)', fontFamily: 'var(--font-body)' }}>{cat}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, color: 'var(--grey-mid)', fontFamily: 'var(--font-body)' }}>{pct}%</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', fontFamily: 'var(--font-body)' }}>{fmt(rev)}</span>
                      </div>
                    </div>
                    <div style={{ width: '100%', height: 6, background: '#f3f0eb', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: color,
                        borderRadius: 3,
                        transition: 'width 0.6s ease',
                        minWidth: rev > 0 ? 6 : 0,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2: Top Products + Avg Order Value + Lead Sources ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 20 }}>

        {/* Top Products */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)', margin: '0 0 20px', fontFamily: 'var(--font-body)' }}>
            Top Products
          </h2>
          {topProducts.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--grey-mid)' }}>No sales data yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {topProducts.map(([name, data], i) => (
                <div key={name} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '13px 0',
                  borderBottom: i < topProducts.length - 1 ? '1px solid #f3f0eb' : 'none',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, color: 'var(--dark)', fontFamily: 'var(--font-body)',
                      fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: 200,
                    }}>
                      {name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 2, fontFamily: 'var(--font-body)' }}>
                      {data.sold} sold
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', fontFamily: 'var(--font-body)', flexShrink: 0, marginLeft: 12 }}>
                    {fmt(data.revenue)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Avg Order Value */}
        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)', margin: '0 0 24px', fontFamily: 'var(--font-body)' }}>
            Avg Order Value
          </h2>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 300,
              color: 'var(--dark)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              marginBottom: 10,
            }}>
              {fmt(avgOrderVal)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--grey-mid)', fontFamily: 'var(--font-body)', lineHeight: 1.6 }}>
              per delivered order
              <br />
              <span style={{ color: 'var(--dark)', fontWeight: 500 }}>{delivered.length}</span> completed orders
            </div>
          </div>

          <div style={{ borderTop: '1px solid #f3f0eb', paddingTop: 16, marginTop: 16, display: 'flex', gap: 16 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--grey-mid)', marginBottom: 4, fontFamily: 'var(--font-body)' }}>Total Revenue</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark)', fontFamily: 'var(--font-body)' }}>
                {fmt(totalRevenue)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--grey-mid)', marginBottom: 4, fontFamily: 'var(--font-body)' }}>Delivered</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark)', fontFamily: 'var(--font-body)' }}>
                {delivered.length}
              </div>
            </div>
          </div>
        </div>

        {/* Lead Sources */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)', margin: '0 0 20px', fontFamily: 'var(--font-body)' }}>
            Lead Sources
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              { label: 'WhatsApp Orders', count: filtered.length },
              { label: 'Enquiries',       count: enqCount        },
              { label: 'Consignments',    count: consCount        },
            ].map((row, i, arr) => (
              <div key={row.label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '13px 0',
                borderBottom: i < arr.length - 1 ? '1px solid #f3f0eb' : 'none',
              }}>
                <span style={{ fontSize: 13, color: row.count > 0 ? 'var(--dark)' : 'var(--grey-mid)', fontFamily: 'var(--font-body)' }}>
                  {row.label}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', fontFamily: 'var(--font-body)' }}>
                  {row.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}